import React from 'react'
import * as S from './style'

interface TableProps {
  children?: React.ReactNode | React.ReactNode[]
  style?: React.CSSProperties
}

const Table = ({ children, style = {} }: TableProps) => (
  <S.Table style={style}>{children}</S.Table>
)

interface ColumnInformation {
  columnName: string
  tooltipMessage?: string
  alert?: boolean
  hidden?: boolean
}

export interface TableHeaderProps {
  columnInformation: ColumnInformation[]
  style?: React.CSSProperties
  flexColumns?: number[]
  columnsBelow?: string[]
  belowAt?: string
}

const resolveColumnsBelow = (columnsBelow: string[], columnData: string, belowAt: string, style?: React.CSSProperties) => (
  columnsBelow.length > 0 && columnData === belowAt && (
    <S.Table border='none'>
      <S.THead borderBottom='none' borderTop='1px solid #c7c9c7' borderRadius={0}>
        <S.THeadTR>
          {columnsBelow.map((belowColumn) => (
            <S.TH key={belowColumn} style={{ ...style }}>
              {belowColumn}
            </S.TH>
          ))}
        </S.THeadTR>
      </S.THead>
    </S.Table>
  )
)

const showColumnName = ({ columnName, tooltipMessage, alert }: ColumnInformation) => (
  tooltipMessage ? (
    <S.ColumnNameWithTooltip alert={alert}>
      {columnName}
      <S.CellTolltip title={tooltipMessage} arrow data-testid='tooltip-hover'>
        <S.InfoIcon/>
      </S.CellTolltip>
    </S.ColumnNameWithTooltip>
  ) : (columnName)
)

const TableHeader = ({
  columnInformation = [], style, flexColumns = [], columnsBelow = [], belowAt = undefined,
}: TableHeaderProps) => (
  <S.THead>
    <S.THeadTR>
      {columnInformation.map((columnData, index) => {
        const isColumnBelowAt: boolean = columnData.columnName === belowAt
        const newStyle = columnData.hidden ? { ...style, display: 'none' } : { ...style }
        return (
          <S.TH key={columnData.columnName} style={newStyle} flex={flexColumns[index]} textAling={isColumnBelowAt ? 'center' : undefined} boxShadow={isColumnBelowAt ? '1px 0px 0px 0px #BCBCBC' : undefined} padding={isColumnBelowAt ? '0px 0px' : undefined}>
            {columnData.columnName === belowAt ? (
              <S.Table border='none'>
                <S.THead borderBottom='none'>
                  <S.THeadTR>
                    <S.TH key={columnData.columnName} style={{ ...style }} textAling='center'>
                      {columnData.columnName}
                    </S.TH>
                  </S.THeadTR>
                </S.THead>
              </S.Table>
            ) : showColumnName(columnData) }
            {resolveColumnsBelow(columnsBelow, columnData.columnName, belowAt ?? '', style)}
          </S.TH>
        )
      })}
    </S.THeadTR>
  </S.THead>
)

interface TableHeadProps {
  children?: React.ReactNode | React.ReactNode[]
}

const TableHead = ({ children }: TableHeadProps) => <S.THead>{children}</S.THead>

interface TableHeadRowProps {
  children?: React.ReactNode | React.ReactNode[]
}

const TableHeadRow = ({ children }: TableHeadRowProps) => <S.THeadTR>{children}</S.THeadTR>

interface TableHeadCellProps {
  children?: React.ReactNode | React.ReactNode[]
  flex?: number
  title?: string
  style?: React.CSSProperties
  isAction?: boolean
}

const TableHeadCell = ({
  children, flex, title = '', style, isAction,
}: TableHeadCellProps) => (
  <S.TH title={title} flex={flex} style={style} isAction={isAction}>
    {children}
  </S.TH>
)

interface TableBodyProps {
  children?: React.ReactNode | React.ReactNode[]
  style?: React.CSSProperties

}

const TableBody = ({ children, style }: TableBodyProps) => <S.TBody style={style}>{children}</S.TBody>

interface TableBodyRowProps {
  isHover?: boolean
  children?: React.ReactNode | React.ReactNode[]
  onClick?: () => void
  cursor?: string
  backgroundColor?: string
  style?: React.CSSProperties
}

const TableBodyRow = ({
  isHover,
  children,
  onClick,
  cursor,
  backgroundColor,
  style,
}: TableBodyRowProps) => (
  <S.TBodyTR
    isHover={isHover}
    onClick={onClick}
    cursor={cursor}
    backgroundColor={backgroundColor}
    style={style}
  >
    {children}
  </S.TBodyTR>
)

export interface TableBodyCellProps {
  children?: React.ReactNode | React.ReactNode[]
  flex?: number
  textAlign?: string
  title?: string
  style?: React.CSSProperties
  onClick?: () => void
  testId?: string
  tooltipMessage?: string,
  isDamage?: boolean
  isAction?: boolean
}

const TableBodyCell = ({
  children,
  flex = undefined,
  textAlign = 'right',
  title = '',
  style = {},
  testId = undefined,
  onClick,
  tooltipMessage,
  isDamage = false,
  isAction,
}: TableBodyCellProps) => (
  <S.TD title={title} flex={flex} textAlign={textAlign} style={style} onClick={onClick} data-testid={testId} isAction={isAction}>
    {tooltipMessage ? (
      <S.CellTolltip title={tooltipMessage} arrow>
        <S.ButtonDate> {children}</S.ButtonDate>
      </S.CellTolltip>
    )
      : (
        <S.Text isDamage={isDamage}>
          {children}
        </S.Text>
      )}
  </S.TD>
)

export {
  Table,
  TableHead,
  TableHeadRow,
  TableHeadCell,
  TableHeader,
  TableBody,
  TableBodyCell,
  TableBodyRow,
}
